import * as React from 'react';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { ComplexValue, RowComponentProps } from 'ui/common/types';
import { ButtonTertiary } from 'ui';
import styles from './tile.scss';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ValidationLevel } from 'ui/common';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { CopyIteratorTranslations } from 'app/area/case/selectors';
import { MdEdit } from 'react-icons/md';
import { ModalSize } from 'app/components/constants';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { LabelSpinner } from 'ui/common/label-spinner/label-spinner';

const DefaultHeader = ({primaryKey, showPrimaryKey}) =>  primaryKey && showPrimaryKey ?
  <div className={`${styles.key}`}>#{primaryKey.value}</div> : null;

class TileComponent extends React.Component<TileProps & OpenStateInjectedProps, TileState> {
  private ref: HTMLElement;
  public static defaultProps: Partial<TileProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };

  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({hideImmediately: false}, () =>
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem()
    );
    !this.props.expandAll && this.ref && scrollIntoView(this.ref);
    this.props.open();
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    this.props.onSelectDocumentItem?.(); //select the active tile to force the route to update itself with the correct section
    this.setState({hideImmediately: true}, () =>
      this.props.onSelectDocumentItem?.(null)); //select null to clear params like 'TblAction/1' etc from the route    
    this.props.close();
  };  

  private renderEditDetailsButton = () => {
    const {shouldEditDocumentItem, textDetailsEditButton, shouldHideEditDocumentIcon} = this.props;   
    const onClickEditIcon = shouldEditDocumentItem ? this.handleEdit : this.handleSelect;

    return <>
      {shouldHideEditDocumentIcon ? null :
        <div className={`${styles.buttonContainer}`} title={textDetailsEditButton} onClick={onClickEditIcon}>
          <MdEdit className={styles.buttonIcons}/> 
        </div>}
    </>;
  };

  private renderViewDetailsButton = () => {
    const { textShowDetails } = this.props;
    const childrenHasErrors = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.error);

    const childrenHasWarnings = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.warning);

    const validationClass = childrenHasErrors ?
      styles.hasChildErrors : (childrenHasWarnings ? styles.hasChildWarnings : '');    
    
    return <>
      <div className={`${styles.buttonContainer}`} onClick={this.handleSelect}>
        <ButtonTertiary classNames={`${styles.detailsButton} ${validationClass}`} onClick={this.handleSelect}>
          {textShowDetails}
        </ButtonTertiary>
      </div>
    </>;
  };

  private renderExtendedButton = () => {
    const { extendedButton } = this.props;
    return extendedButton?.visible ?
      <div className={`${styles.buttonContainer}`}>
        <ButtonTertiary
          classNames={`${styles.extendedButton}`}
          disabled={extendedButton.loading}
          onClick={extendedButton.onClick}>
          {extendedButton.loading ? <LabelSpinner /> : extendedButton.text}
        </ButtonTertiary>
      </div> : null;
  };

  private renderExtendedButtonExpandAll = () => {
    const { extendedButton } = this.props;
    return extendedButton?.visible ?
      <ButtonSecondary classNames={styles.editButton} disabled={extendedButton.loading} onClick={extendedButton.onClick}>
        {extendedButton.loading ? <LabelSpinner /> : extendedButton.text}
      </ButtonSecondary> : null;
  };

  private addRef = (el: HTMLDivElement | null) => {
    this.ref = el;
  };

  // The Tile has become to procedurally heavy.
  // It takes on too many concerns and carries the logical weight of all it's use cases combined.
  // Refactoring should take place when changing the UX from expander to modal dialogs.
  // The inline comments are added to assist that refactoring later on.

  private getDialogTitle = () => {
    const { textKeyLabel, primaryKey } = this.props;
    return `${textKeyLabel} #${(primaryKey && primaryKey.value) || ''}`;
  };

  /**
   * Whether the details of the tile are shown (expand all, print or activated) and whether it is the activated tile.
   */
  private getDetailsState = () => {
    const { details, activate, showDetailsButton = true, print, expandAll } = this.props;
    const canActivate = showDetailsButton && !this.state.hideImmediately;
    const isActivated = canActivate && activate;
    const shouldRenderOpenDetailView = !!(details && (print || expandAll || isActivated));
    return { isActivated, shouldRenderOpenDetailView };
  };

  private renderDetailsDialogEditButton = (shouldRenderOpenDetailView: boolean) => {
    const { showDetailsEditButton = false, textDetailsEditButton } = this.props;
    return shouldRenderOpenDetailView && showDetailsEditButton ?
      <ButtonSecondary classNames={styles.editButton} onClick={this.handleEdit}>{textDetailsEditButton}</ButtonSecondary> : null;
  };

  private renderDetailViewExpanded = (detailsEditButton: React.JSX.Element | null) => {
    const { details, textDone, expandAll } = this.props;
    return <DialogExpander
      expandAll={true}
      onClose={this.handleClose}
      title={this.getDialogTitle()}
      hideTitle={false}
      textDone={textDone}
      extraFooterActions={[detailsEditButton]}
      className="tile-details-view">
      {expandAll && <ContextMenu {...this.props} onDelete={this.handleDelete}/>}
      {details}
      {expandAll &&
        <div className={styles.editButtonExpandAll}>
          {this.renderExtendedButtonExpandAll()}
          {detailsEditButton}
        </div>}
    </DialogExpander>;
  };

  private renderDetailView = (detailsEditButton: React.JSX.Element | null) => {
    const { details, textDone } = this.props;
    return <ModalWrapper
      onClose={this.handleClose}
      onDone={this.handleClose}
      headerTitle={this.getDialogTitle()}
      btnTitle={textDone}
      btnExtraFooterActionOverride={detailsEditButton}
      size={ModalSize.xtraLarge}
      textClose={textDone}>
      {details}
    </ModalWrapper>;
  };

  private getTileWrapperClasses = (shouldRenderOpenDetailView: boolean) => {
    const openDetailWrapperStyles = shouldRenderOpenDetailView && !this.props.expandAll ? styles.activeTile : '';
    return ` tile ${this.props.className} ${styles.tileWrapper} ${openDetailWrapperStyles}`;
  };

  private getTileClassNames = () => {
    const { showDetailsButton = true, details, expandAll } = this.props;
    const contextMenuClassNames = this.props?.enabledChildEntityOperations?.remove ? styles.hasMeatball : '';
    const withShowDetailsButtonStyle = showDetailsButton && details && !expandAll && styles.withShowDetailsButton;
    return `${styles.tile} ${contextMenuClassNames} ${withShowDetailsButtonStyle}`;
  };

  private getTileContentClassNames = () => {
    const { showPrimaryKey = true, primaryKey, children } = this.props;
    const withKey = (showPrimaryKey && primaryKey) ? styles.tileWithKey : '';
    const titleStyle = React.Children.toArray(children).length > 1 ? 'has-title' : '';
    return {
      content: `${styles.tileContent} ${withKey}`,
      values: `tilevalues ${styles.tileValuesWrapper} ${titleStyle}`
    };
  };

  private renderButtons = () => {
    const { showDetailsButton = true, showDetailsEditButton = false, details } = this.props;
    const viewDetailsButton = showDetailsButton && details && this.renderViewDetailsButton();
    const editDetailsButton = showDetailsEditButton && details && this.renderEditDetailsButton();

    return <div className={styles.btnIconsWrapper}>
      {this.renderExtendedButton()}
      {viewDetailsButton}
      {editDetailsButton}
      <ContextMenu {...this.props} onDelete={this.handleDelete}/>
    </div>;
  };

  // Only show details - no header, no context menu - no modal backdrop
  private renderExpandedDetailsOnly = () => {
    const { componentId } = this.props;
    const { isActivated, shouldRenderOpenDetailView } = this.getDetailsState();
    const detailsEditButton = this.renderDetailsDialogEditButton(shouldRenderOpenDetailView);

    return <div id={componentId} ref={this.addRef} className={this.getTileWrapperClasses(shouldRenderOpenDetailView)}>
      {this.renderDetailViewExpanded(detailsEditButton)}
      {isActivated &&
        <span className={styles.activatedDetailView}>
          {this.renderDetailView(detailsEditButton)}
        </span>}
    </div>;
  };

  public render() {
    const { componentId, children, print, expandAll, details } = this.props;

    if (expandAll && details) {
      return this.renderExpandedDetailsOnly();
    }

    const { shouldRenderOpenDetailView } = this.getDetailsState();
    const detailsEditButton = this.renderDetailsDialogEditButton(shouldRenderOpenDetailView);
    const classNames = this.getTileContentClassNames();

    return <>
      {/* When expanded/open - render the close handle before the tile */}
      {shouldRenderOpenDetailView ? <div tabIndex={0} onFocus={this.handleClose}/> : ''}

      <div id={componentId} className={this.getTileWrapperClasses(shouldRenderOpenDetailView)}>
        <div className={this.getTileClassNames()}>
          <div className={classNames.content}>
            {/* Tile content - header, context menu and children*/}
            <DefaultHeader {...this.props as any} />
            {this.renderButtons()}
            <div className={classNames.values}>
              {children}
            </div>
          </div>
        </div>
        {/* If expanded (active, expand-all, print) - renders the details */}
        {shouldRenderOpenDetailView && this.renderDetailView(detailsEditButton)}
        {/* Modal backdrop */}
        {(shouldRenderOpenDetailView && !(print || expandAll)) && <ExpanderBackground onClick={this.handleClose}/>}
      </div>
    </>;
  }
}

/**
 * Required translated texts for this component
 */
export interface TileTranslations {
  textDelete: string;
  textDeleteMessage: string;
  textContinue: string;
  textCancel: string;
  textKeyLabel?: string;
  textDone?: string;
  textShowDetails?: string;
  textHideDetails?: string;
  textDetailsEditButton?: string;
}

/**
 * @interface TileProps
 * Required properties for Tile component.
 */
export interface TileProps extends RowComponentProps, TileTranslations {
  primaryKey: ComplexValue<number>;
  showPrimaryKey?: boolean;
  showPrimaryKeyLongDescription?: boolean;
  linkifyLongDescription?: boolean;
  showDetailsButton?: boolean;
  showDetailsEditButton?: boolean;
  extendedContextMenuOperations?: { type: string; text: string; callback: () => void; icon?: React.ReactNode }[];
  print: boolean;
  expandAll: boolean;

  copyIterator?: CopyIteratorTranslations;
  textSubmit?: string;
  textSubmitInfo?: string;
  onSubmitDocument: (continueEditing?: boolean) => void;
  textDeleteCaseCancel?: string;
  textDeleteCaseCancelInfo?: string;
  onCloseDocument: () => void;

  shouldEditDocumentItem?: boolean;
  shouldRenderSubmitButton?: boolean;
  shouldHideEditDocumentIcon?: boolean;
  fetching?: boolean;
  disabledOverlay?: boolean;
  showDetailsHeader?: boolean;
  url?: string;
  params?: { favouriteId?: number; dashboardType?: number; bowtieCaseId?: number; id?: number };
  extendedButton?: {text: string, visible?: boolean, loading?: boolean, onClick: () => void};
}

export interface TileState {
  /**
   * When expanded, don't wait the redux store update. Just close the view. UX optimization.
   */
  hideImmediately?: boolean;
}

const t = withOpenState()(debounceRender(TileComponent));
export {t as Tile};
